import React, { useEffect, useState, useContext } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Avatar,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  GridItem,
  Spinner,
  useToast,
  Badge,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from "@chakra-ui/react";

import { useParams, useNavigate } from "react-router-dom";
import {
  fetchUserDetailsById,
  suspendAdminById,
  deleteAdminUser,
} from "../../actions/users.action";
import PasswordModal from "../../components/ChangePasswordModal";
import { AuthContext } from "../../providers/AuthValidationLayer";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import { Loading } from "../../components/Loading";

export default function AdminDetail() {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();

  const { user, hasPermission } = useContext(AuthContext);

  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNameExpanded, setIsNameExpanded] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // ---------------- DERIVED FLAGS ----------------
  const isOwnProfile = user?._id === admin?._id;

  const isSuperAdmin = admin?.roleId?.roleName?.toLowerCase() === "superadmin";

  // base permissions
  const canEditBase = hasPermission("Access Control", "Employees", "PATCH");
  const canSuspendBase = hasPermission("Access Control", "Employees", "PATCH");
  const canDeleteBase = hasPermission("Access Control", "Employees", "DELETE");

  // final permissions (system role protected)
  const canEdit = canEditBase && !isSuperAdmin;
  const canSuspend = canSuspendBase && !isSuperAdmin && !isOwnProfile;
  const canDelete = canDeleteBase && !isSuperAdmin;

  const canResetPassword =
    isOwnProfile ||
    (hasPermission("Access Control", "Employees", "PATCH") && !isSuperAdmin);

  // ---------------- API CALLS ----------------
  const getAdminDetails = async () => {
    try {
      setLoading(true);
      const data = await fetchUserDetailsById(id);
      setAdmin(data);
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to fetch admin details",
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSuspend = async () => {
    if (isOwnProfile) {
      toast({
        title: "Action not allowed",
        description: "You cannot suspend or unsuspend your own account.",
        status: "warning",
      });
      return;
    }

    try {
      setActionLoading(true);

      const res = await suspendAdminById(admin._id);

      toast({
        title: "Success",
        description: res.message,
        status: "success",
      });

      setAdmin((prev) => ({
        ...prev,
        isSuspended: !prev.isSuspended,
      }));
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to update status",
        status: "error",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;
    try {
      await deleteAdminUser(admin._id);

      toast({
        title: "Admin deleted",
        status: "success",
      });

      navigate("/settings/admin/manage");
    } catch (err) {
      toast({
        title: "Error",
        description: err.message || "Failed to delete admin",
        status: "error",
      });
    }
  };

  useEffect(() => {
    getAdminDetails();
  }, [id]);

  if (loading) {
    return <Loading />;
  }

  if (!admin) return null;

  return (
    <Box w="100%" bg="#F5F7FA" p={6}>
      {/* BREADCRUMB */}
      <BreadCrumbMaker
        items={[
          { title: "Access Control", link: "/settings" },
          { title: "Manage Employee", link: "/settings/admin/manage" },
          { title: "View Employee", link: `/settings/admin/${id}` },
        ]}
      />
      {isSuperAdmin && (
        <Alert
          status="warning"
          borderRadius="12px"
          mt={4}
          bg="#FFF7ED"
          border="1px solid #FDBA74"
        >
          <AlertIcon color="#EA580C" />
          <Box>
            <AlertTitle fontWeight="700" color="#9A3412">
              System Role
            </AlertTitle>
            <AlertDescription color="#9A3412">
              This is a system role. You cannot edit or suspend this account.
            </AlertDescription>
          </Box>
        </Alert>
      )}

      {/* HEADER */}
      <Box bg="white" borderRadius="16px" p={6} mt={4} overflow="hidden">
        <Flex justify="space-between" align="center" gap={4}>
          <Flex align="center" gap={4} flex={1} minW={0}>
            <Avatar size="xl" name={admin.name} bg="#4C7CF7" color="white" />
            <Box minW={0}>
              <Flex align="center" gap={3} minW={0}>
                <Text
                  fontSize="2xl"
                  fontWeight="700"
                  isTruncated
                  maxW="100%"
                  title={admin.name}
                >
                  {admin.name}
                </Text>
                <Badge
                  colorScheme={admin.isSuspended ? "red" : "green"}
                  fontSize="sm"
                  flexShrink={0}
                >
                  {admin.isSuspended ? "Suspended" : "Active"}
                </Badge>
              </Flex>
              <Text
                color="gray.600"
                fontSize="sm"
                isTruncated
                maxW="100%"
                title={admin.email}
              >
                {admin.email}
              </Text>
              <Badge colorScheme="blue" mt={2} isTruncated maxW="100%">
                <Text isTruncated maxW="100%" title={admin.roleId?.roleName}>
                  {admin.roleId?.roleName}
                </Text>
              </Badge>
            </Box>
          </Flex>

          {/* ACTION BUTTONS */}
          <Flex gap={3}>
            {canEdit && (
              <Button
                isDisabled={admin.isSuspended}
                onClick={() => navigate(`/settings/admin/edit/${admin._id}`)}
                colorScheme="blue"
              >
                Edit Employee
              </Button>
            )}

            {canResetPassword && (
              <Button
                onClick={() => setShowPasswordModal(true)}
                colorScheme="blue"
              >
                {isOwnProfile ? "Change Password" : "Reset Password"}
              </Button>
            )}

            {canSuspend && (
              <Button
                colorScheme={admin.isSuspended ? "green" : "red"}
                onClick={toggleSuspend}
                isLoading={actionLoading}
              >
                {admin.isSuspended ? "Unsuspend" : "Suspend"}
              </Button>
            )}

            {canDelete && admin.isSuspended && (
              <Button colorScheme="red" onClick={handleDelete}>
                Delete Employee
              </Button>
            )}
          </Flex>
        </Flex>

        {/* TABS */}

        <Tabs mt={8} variant="unstyled">
          <TabList
            bg="#EEF2FF"
            p={2}
            borderRadius="14px"
            display="grid"
            gridTemplateColumns="repeat(4, 1fr)"
          >
            {["Employee Details", "Employment Details", "Emergency Contact", "Role & Access"].map((tab) => (
              <Tab
                key={tab}
                py={3}
                fontSize="20px"
                fontWeight="600"
                borderRadius="12px"
                _selected={{ bg: "white" }}
              >
                {tab}
              </Tab>
            ))}
          </TabList>

          <TabPanels>
            {/* Employee Details Tab */}
            <TabPanel>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(4,1fr)' }} gap={6}>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Full Name</Text>
                  <Text>{admin.name}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Email ID</Text>
                  <Text>{admin.email}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Mobile Number</Text>
                  <Text>{admin.mobileNumber ? `+91 ${admin.mobileNumber}` : ''}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">WhatsApp Number</Text>
                  <Text>{admin.mobileNumber ? `+91 ${admin.mobileNumber}` : ''}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Date of Birth</Text>
                  <Text>
                    {admin.dateOfBirth
                      ? (() => { try { return new Date(admin.dateOfBirth).toISOString().split("T")[0]; } catch { return "—"; } })()
                      : "—"}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Date of Joining</Text>
                  <Text>
                    {admin.dateOfJoining
                      ? (() => { try { return new Date(admin.dateOfJoining).toISOString().split("T")[0]; } catch { return "—"; } })()
                      : "—"}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Gender</Text>
                  <Text>{admin.gender}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Language</Text>
                  <Text>
                    {Array.isArray(admin.languageKnown)
                      ? admin.languageKnown.filter(Boolean).join(", ")
                      : (typeof admin.languageKnown === 'string' && admin.languageKnown.includes(","))
                        ? admin.languageKnown.split(",").map(s => s.trim()).filter(Boolean).join(", ")
                        : admin.languageKnown || "—"}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">State</Text>
                  <Text>{admin.state}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">City</Text>
                  <Text>{admin.city}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Pincode</Text>
                  <Text>{admin.pincode}</Text>
                </GridItem>
                <GridItem colSpan={4}>
                  <Text fontWeight="600" color="gray.500" mt={4}>Address</Text>
                  <Text>{admin.address}</Text>
                  {/* Address Proof Files Preview */}
                  {(admin.addressProofFiles && admin.addressProofFiles.length > 0) || (admin.idProofFiles && admin.idProofFiles.length > 0) ? (
                    <Flex mt={2} gap={10} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                      {admin.addressProofFiles && admin.addressProofFiles.length > 0 && (
                        <Box>
                          <Text fontWeight="600" color="gray.500" mb={1}>Address Proof Files</Text>
                          <Flex gap={6} flexWrap="nowrap" overflowX="auto">
                            {admin.addressProofFiles.map((file, index) => (
                              <Box key={index} mt={1} borderWidth={1} borderRadius="md" p={2} minW="180px" maxW="220px" textAlign="center" bg="#F9FAFB">
                                {file.match(/\.(jpg|jpeg|png|gif|svg)$/i) ? (
                                  <img src={file} alt={`Address Proof ${index + 1}`} style={{ maxWidth: 180, maxHeight: 180, margin: '0 auto', display: 'block', borderRadius: 8 }} />
                                ) : file.match(/\.pdf$/i) ? (
                                  <embed src={file} type="application/pdf" width="180" height="180" style={{ borderRadius: 8 }} />
                                ) : (
                                  <Text color="gray.400">Preview not available</Text>
                                )}
                                <Text fontSize="xs" mt={1}>Address Proof {index + 1}</Text>
                              </Box>
                            ))}
                          </Flex>
                        </Box>
                      )}
                      {admin.idProofFiles && admin.idProofFiles.length > 0 && (
                        <Box>
                          <Text fontWeight="600" color="gray.500" mb={1}>ID Proof Files</Text>
                          <Flex gap={6} flexWrap="nowrap" overflowX="auto">
                            {admin.idProofFiles.map((file, index) => (
                              <Box key={index} mt={1} borderWidth={1} borderRadius="md" p={2} minW="180px" maxW="220px" textAlign="center" bg="#F9FAFB">
                                {file.match(/\.(jpg|jpeg|png|gif|svg)$/i) ? (
                                  <img src={file} alt={`ID Proof ${index + 1}`} style={{ maxWidth: 180, maxHeight: 180, margin: '0 auto', display: 'block', borderRadius: 8 }} />
                                ) : file.match(/\.pdf$/i) ? (
                                  <embed src={file} type="application/pdf" width="180" height="180" style={{ borderRadius: 8 }} />
                                ) : (
                                  <Text color="gray.400">Preview not available</Text>
                                )}
                                <Text fontSize="xs" mt={1}>ID Proof {index + 1}</Text>
                              </Box>
                            ))}
                          </Flex>
                        </Box>
                      )}
                    </Flex>
                  ) : null}
                </GridItem>
              </Grid>
            </TabPanel>

            {/* Employment Details Tab */}
            <TabPanel>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(4,1fr)' }} gap={6}>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Employee ID</Text>
                  <Text>{admin.employeeId || '—'}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Department</Text>
                  <Text>{admin.department || '—'}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Team</Text>
                  <Text>{admin.team || '—'}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Designation</Text>
                  <Text>{admin.designation || '—'}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Branch</Text>
                  <Text>{admin.branch || '—'}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Shift Timings</Text>
                  <Text>{admin.shiftTimings || '—'}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Date of Joining</Text>
                  <Text>
                    {admin.dateOfJoining
                      ? (() => { try { return new Date(admin.dateOfJoining).toISOString().split("T")[0]; } catch { return "—"; } })()
                      : "—"}
                  </Text>
                </GridItem>
              </Grid>
            </TabPanel>
            {/* Emergency Contact Tab */}
            <TabPanel>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(3,1fr)' }} gap={6}>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Emergency Contact Name</Text>
                  <Text>{admin.emergencyContactName || '—'}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Emergency Contact Relation</Text>
                  <Text>{admin.emergencyContactRelation || '—'}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="600" color="gray.500">Emergency Contact Number</Text>
                  <Text>{admin.emergencyContactNumber || '—'}</Text>
                </GridItem>
              </Grid>
            </TabPanel>

            {/* Role & Access Tab */}
            <TabPanel>
              <TableContainer>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Permission</Th>
                      <Th textAlign="center">POST</Th>
                      <Th textAlign="center">GET</Th>
                      <Th textAlign="center">PATCH</Th>
                      <Th textAlign="center">DELETE</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {admin.roleId?.roleAccess?.map((section) =>
                      section.types.map((type) => (
                        <Tr key={type._id}>
                          <Td>{type.subHeading}</Td>
                          {["POST", "GET", "PATCH", "DELETE"].map((a) => (
                            <Td
                              key={a}
                              textAlign="center"
                              color={
                                type.access.includes(a)
                                  ? "green.500"
                                  : "gray.300"
                              }
                            >
                              {type.access.includes(a) ? "✓" : "—"}
                            </Td>
                          ))}
                        </Tr>
                      )),
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* PASSWORD MODAL */}
        <PasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          isOwnPassword={isOwnProfile}
          adminId={!isOwnProfile ? admin._id : null}
        />
      </Box>
    </Box>
  );
}

// Helper for label/value display
const DetailRow = ({ label, value }) => (
  <Grid templateColumns="160px 1fr" alignItems="center" mb={2}>
    <Text fontWeight="600" color="gray.500">{label}</Text>
    <Text>{value || <span style={{ color: '#bbb' }}>—</span>}</Text>
  </Grid>
);
